import { useState } from 'react'
import { createModel } from 'hox'
import { Isubject, TeacherCommon } from '@/types/login'
import { KeyValuePair } from '@/types/common'
import { ERROR_CODE, getTokenTenantId } from '@yuanfudao/metis-unified-login'
import { PersonalAccountVO, UserTenant } from '@yuanfudao/metis-unified-login/es/types/login'
import { accountInstance, permissionInstance, UNIVERSAL_CONTEXT } from '@yuanfudao/metis-standard-data'

function useTeacherInfo() {
  const [userTenants, setUserTenants] = useState<UserTenant[]>([])
  const [currentAccount, setCurrentAccount] = useState<PersonalAccountVO | null>()
  const [accountAvartar, setAccountAvartar] = useState('')
  const [commonInfo, setCommonInfo] = useState<TeacherCommon>()
  const [accountName, setAccountName] = useState('')

  // 只定义更新方法，但不在这里调用，统一放到hox.ts去向外暴露
  // 老师在该学校下面的常用信息，入teacherId，inTenantName
  const updateTeacherCommonInfo = async () => {
    try {
      const curTid = getTokenTenantId()
      const inTenantId = curTid ? String(curTid) : ''
      if (inTenantId && inTenantId !== '-1') {
        const [account, perKeys, perTree] = await Promise.all([
          accountInstance.updateAccount(),
          permissionInstance.updatePermissionKeys(),
          permissionInstance.updatePermissionTree(),
        ])
        const res = {
          inTenantName: account.teacherName,
          permissionKeys: perKeys,
          permissions: perTree,
          subjectList: account.teacherSubjectList,
          teacher: {
            avatar: account.teacherAvatar,
            muid: account.teacherId,
            name: account.teacherName,
            phone: account.teacherPhone,
            tenantId: account.teacherTenantId,
          },
          tenant: {
            name: account.teacherTenantName,
            regionId: account.teacherTenantRegionId,
            phaseList: account.teacherTenantPhaseList,
            muid: account.teacherTenantId,
          },
        } as TeacherCommon

        if (res) {
          setCommonInfo(res)
          const { muid: teaId } = res.teacher
          const { subjectList } = res
          let subjectObj: KeyValuePair<Isubject> = {}
          if (localStorage.getItem(btoa('currentSubject'))) {
            subjectObj = JSON.parse(localStorage.getItem(btoa('currentSubject')) as string)
            if (subjectObj[teaId] && subjectObj[teaId].subjectId) {
              // @ts-ignore
              const obj = subjectList.filter((item) => subjectObj[teaId].subjectId === item.subjectId)
              if (obj && obj.length > 0) {
                return res
              }
            }
          }
          // @ts-ignore
          subjectObj[teaId] = subjectList[0]
          localStorage.setItem(btoa('currentSubject'), JSON.stringify(subjectObj))
        }
        // 返回请求结果，以便可以在调用更新方法的时候实时拿到最新数据
        return res
      }
      return new Error('inTenantId === -1 未选择学校')
    } catch (e) {
      console.warn(e)
      return new Error('updateTeacherCommonInfo 错误')
    }
  }

  const updateUserTenants = async () => {
    try {
      const result = await accountInstance.updateUserTenants()
      setUserTenants(result)
      // 返回请求结果，以便可以在调用更新方法的时候实时拿到最新数据
      return result
    } catch (e: any) {
      const { code } = e.cause || {}
      if ([ERROR_CODE.NO_PERMISSION, ERROR_CODE.NO_PERMISSION2].includes(code as ERROR_CODE)) {
        return code
      }
      if (e?.response?.status === 403 || [ERROR_CODE.NO_PERMISSION, ERROR_CODE.NO_PERMISSION2].includes(e?.response?.data?.code)) {
        return e.response
      }
      console.warn(e)
      return new Error('updateRoles 错误')
    }
  }

  const updateCurrentAccount = async () => {
    try {
      const result = await accountInstance.updateAccount(UNIVERSAL_CONTEXT.ACCOUNT)
      if (result) {
        const { accountName: an, accountPhone } = result
        let tmpName = an || ''
        if (!an) {
          tmpName = `尾号${(accountPhone || '').slice(-4)}`
        }
        setAccountName(tmpName)
        setAccountAvartar(result.accountAvatar || '')
        setCurrentAccount({
          accountId: result.accountId as number,
          avatar: result.accountAvatar as string,
          email: '',
          nickname: result.accountName as string,
          phone: result.accountPhone as string,
          wxBound: result.accountWxBound as boolean,
          wxNickName: result.accountWxNickname as string,
          hasPassword: result.accountHasPassword as boolean,
        })
      }
      return result
    } catch (e: any) {
      const { code } = e.cause || {}
      if ([ERROR_CODE.NO_PERMISSION, ERROR_CODE.NO_PERMISSION2].includes(code as ERROR_CODE)) {
        return code
      }
      if (e?.response?.status === 403 || [ERROR_CODE.NO_PERMISSION, ERROR_CODE.NO_PERMISSION2].includes(e?.response?.data?.code)) {
        return e.response
      }
      console.warn(e)
      return new Error('updateTeacherDetailByAccount 错误')
    }
  }

  // 只定义更新方法，但不在这里调用，统一放到hox.ts去向外暴露
  const updateTotalTeacherInfo = async () => {
    const result = await Promise.all([
      updateTeacherCommonInfo(),
      updateUserTenants(),
      updateCurrentAccount(),
    ])
    // 返回请求结果，以便可以在调用更新方法的时候实时拿到最新数据
    return {
      teacherCommonInfo: result[0],
      userTenants: result[1],
      currentAccount: result[2],
    }
  }

  // 不对外暴露setState，只暴露state和接口请求的方法，统一用接口请求来更新state
  return {
    accountAvartar,
    accountName,
    teacherCommonInfo: commonInfo,
    userTenants,
    currentAccount,
    updateTeacherCommonInfo,
    updateTotalTeacherInfo,
    updateUserTenants,
    updateCurrentAccount,
  }
}

export default createModel(useTeacherInfo)
